import * as React from "react";
import { BellIcon } from "lucide-react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ibada/ui";

import { useTranslations } from "@/i18n";
import { NotificationBadge } from "@/features/notifications/components/notification-badge";
import { NotificationDropdown } from "@/features/notifications/components/notification-dropdown";
import { MobileNotificationsScreen } from "@/features/notifications/components/mobile-notifications-screen";
import {
  useNotificationsUnreadCountQuery,
} from "@/features/notifications/services/notifications-query";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import { cn } from "@/shared/lib/utils";

/**
 * `triggerClassName` restyles the bell button only (the phone header draws it as a bordered circle); `icon`
 * replaces the line bell (the phone header uses the coloured one).
 */
export function NotificationBell({ triggerClassName, icon }: { triggerClassName?: string; icon?: React.ReactNode } = {}) {
  const t = useTranslations("notifications");
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const unreadCountQuery = useNotificationsUnreadCountQuery();
  const unreadCount = unreadCountQuery.data?.unreadCount ?? 0;

  const bellIconClassName = isMobile ? "size-6 shrink-0" : "size-4 shrink-0";
  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
  }, []);

  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size={isMobile ? "icon" : "icon-sm"}
      className={cn("relative shrink-0 overflow-visible", triggerClassName)}
      aria-label={t("bell.label")}
    >
      {icon ?? <BellIcon className={bellIconClassName} />}
      <NotificationBadge count={unreadCount} />
    </Button>
  );

  // Phone: the bell opens a full notifications page (MobileNotificationsScreen), not a side panel.
  if (isMobile) {
    return (
      <>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("relative shrink-0 overflow-visible", triggerClassName)}
          aria-label={t("bell.label")}
          onClick={() => handleOpenChange(true)}
        >
          {icon ?? <BellIcon className={bellIconClassName} />}
          <NotificationBadge count={unreadCount} />
        </Button>
        <MobileNotificationsScreen open={open} onOpenChange={handleOpenChange} />
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="w-[28rem] max-w-[calc(100vw-1rem)] p-0">
        <NotificationDropdown onOpenChange={handleOpenChange} />
      </PopoverContent>
    </Popover>
  );
}
